import type { FC } from 'react'
import orderBy from 'lodash/fp/orderBy'
import { Box, Typography } from '@mui/material'
import DevicesOtherIcon from '@mui/icons-material/DevicesOther'
import { PatientUser } from '~/shared/types'
import { DetailGrid, EmptyState, SectionCard, type DetailGridItem } from '~/shared/ui'
import { asDisplayText } from '~/shared/lib/displayText'
import { isRecord } from '~/shared/lib/guards'

// The device rows arrive camelized on the legacy Redux patient record and are
// not part of the typed PatientUser surface; treat them as loose objects the
// same way the legacy tab does.
type LooseRecord = Record<string, unknown>

interface PatientDetailMetaProps {
  patient: PatientUser & { userdevices?: LooseRecord[] }
}

// Keep the scalar fields worth reading; arrays/objects and audit columns are
// debugging noise (the legacy tab dumps them; Django Admin covers that need).
const scalarItems = (obj: LooseRecord | null | undefined): DetailGridItem[] =>
  Object.keys(obj ?? {})
    .filter(key => !['updatedAt', 'updatedBy', 'createdAt', 'createdBy'].includes(key))
    .sort()
    .flatMap(key => {
      const value = (obj as LooseRecord)[key]
      // A field the device reported as empty still belongs in a debug view, so an absent value
      // keeps its row. Only values with no useful string form are dropped.
      if (value == null) return [{ label: key, value: '—', mono: true }]
      const text = asDisplayText(value)
      return text === null ? [] : [{ label: key, value: text, mono: true }]
    })

/**
 * Device tab: the member's registered devices (type, app version, OS,
 * permissions) as one card per device, plus the person id for
 * cross-referencing. Deliberately not a full DB-row dump — engineers debug
 * those raw rows in Django Admin.
 */
const PatientDetailMeta: FC<PatientDetailMetaProps> = ({ patient }) => {
  const devices = orderBy(
    device => device.updatedAt as string | undefined,
    ['desc'],
    patient.userdevices ?? [],
  )

  return (
    <Box sx={{ px: 3, pt: 1.5, pb: 5, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Typography sx={{ fontSize: '1.25rem', color: 'text.secondary' }}>
          {devices.length} {devices.length === 1 ? 'device' : 'devices'}
        </Typography>
        <Typography
          sx={theme => ({
            fontSize: '1.25rem',
            color: 'text.secondary',
            fontFamily: theme.typography.fontFamilyMono,
          })}
        >
          Person ID: {patient.person.id}
        </Typography>
      </Box>

      {devices.length === 0 ? (
        <EmptyState
          icon={<DevicesOtherIcon />}
          title="No devices recorded"
          caption="The member has not signed in on a registered device yet."
        />
      ) : (
        devices.map(device => (
          <SectionCard key={String(device.deviceUniqueId)}>
            <Typography
              sx={{ fontSize: '1.5rem', fontWeight: 600, color: 'text.primary', mb: 1.5 }}
            >
              Device: {asDisplayText(device.deviceType) ?? 'Unknown'}
            </Typography>
            <DetailGrid items={scalarItems(device)} columns={3} />
            {isRecord(device.permissions) && Object.keys(device.permissions).length > 0 ? (
              <>
                <Typography
                  sx={{
                    fontSize: '1.3rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                    mt: 2,
                    mb: 1,
                  }}
                >
                  Permissions
                </Typography>
                <DetailGrid items={scalarItems(device.permissions)} columns={3} />
              </>
            ) : null}
          </SectionCard>
        ))
      )}
    </Box>
  )
}

export default PatientDetailMeta
